/**
 * Pick an image - a menu photo, or the business logo on the Settings screen.
 *
 * The file is uploaded the moment it is chosen and the returned path is handed
 * back through `onChange`. Uploading on selection rather than on save is what
 * makes the preview truthful: what you are looking at is the file as the server
 * stored it, not a browser blob that vanishes on refresh and leaves you
 * wondering whether it saved.
 *
 * The trade is that abandoning a dialog can leave a file on disk with nothing
 * pointing at it. Replacing an image cleans up the one it replaced when that
 * one was uploaded in this same sitting - the server refuses to delete anything
 * an item still points at, so this can ask without having to know what is saved.
 */

import { useRef, useState } from 'react';
import { menuApi } from '../../services/endpoints';
import { assetUrl } from '../../services/api';

/** Same list the server accepts. Kept in step by hand; it is four values. */
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

const ImagePicker = ({
    value,
    onChange,
    disabled = false,
    hint = 'Shown on the till. A square photo around 400x400 looks best.',
    // What the buttons call the thing, so the logo picker does not offer to
    // change a "photo".
    noun = 'photo',
    // Which endpoint the bytes go to. Menu photos and logos land in different
    // folders behind different permissions.
    upload = menuApi.uploadImage,
    // Cleaning up an orphan goes through the same permission as uploading one.
    remove = menuApi.deleteImage,
    // A logo is usually wide and must not be cropped to a square; a menu photo
    // is a tile and should fill it.
    fit = 'cover',
    boxClassName = 'h-24 w-24'
}) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Paths uploaded during this sitting.
     *
     * Only these are offered up for deletion. A path that arrived with the item
     * was saved by someone at some point, and quietly deleting it because a
     * dialog was opened would be a surprise nobody asked for.
     */
    const uploadedHere = useRef(new Set());

    const discard = (path) => {
        if (!path || !uploadedHere.current.has(path)) return;
        uploadedHere.current.delete(path);
        // Fire and forget: the file is already replaced on screen, and a failed
        // cleanup is a stray file, not something to interrupt the user over.
        remove(path).catch(() => {});
    };

    const handleFile = async (file) => {
        if (!file) return;

        setError(null);
        setBusy(true);
        const previous = value;

        try {
            const result = await upload(file);
            uploadedHere.current.add(result.path);
            onChange(result.path);
            discard(previous);
        } catch (err) {
            setError(err.message || 'That image could not be uploaded.');
        } finally {
            setBusy(false);
            // Clear the input so choosing the same file twice still fires a
            // change event - which is what happens when a first attempt failed.
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const handleRemove = () => {
        const removed = value;
        setError(null);
        onChange(null);
        discard(removed);
    };

    return (
        <div>
            <div className="flex items-start gap-4">
                {/* The preview doubles as the button, because a photo is a
                    bigger and more obvious target than the word "Browse". */}
                <button
                    type="button"
                    disabled={disabled || busy}
                    onClick={() => inputRef.current?.click()}
                    className={`relative flex ${boxClassName} shrink-0 items-center justify-center overflow-hidden
                                rounded-xl border-2 border-dashed bg-slate-50 text-slate-400 transition-colors
                                ${disabled || busy
                                    ? 'cursor-wait border-slate-200'
                                    : 'border-slate-300 hover:border-brand-400 hover:text-brand-500'}`}
                    aria-label={value ? `Change the ${noun}` : `Add a ${noun}`}
                >
                    {value ? (
                        <img
                            src={assetUrl(value)}
                            alt=""
                            className={`h-full w-full ${fit === 'contain' ? 'object-contain p-1' : 'object-cover'}`}
                        />
                    ) : (
                        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 6h.008v.008H18V6zm2.25 12V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25h12A2.25 2.25 0 0020.25 18z" />
                        </svg>
                    )}

                    {busy && (
                        <span className="absolute inset-0 flex items-center justify-center bg-white/75
                                         text-xs font-medium text-slate-600">
                            Uploading…
                        </span>
                    )}
                </button>

                <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={disabled || busy}
                            onClick={() => inputRef.current?.click()}
                        >
                            {value ? `Change ${noun}` : `Upload ${noun}`}
                        </button>

                        {value && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50"
                                disabled={disabled || busy}
                                onClick={handleRemove}
                            >
                                Remove
                            </button>
                        )}
                    </div>

                    {error
                        ? <p className="text-sm text-red-600">{error}</p>
                        : <p className="text-xs text-slate-500">{hint}</p>}
                </div>
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(event) => handleFile(event.target.files?.[0])}
            />
        </div>
    );
};

export default ImagePicker;
